import clsx from 'clsx';
import { type ComponentType, type InputHTMLAttributes, type ReactNode } from 'react';
import { type FieldErrors, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import Tooltip from '@/components/Ui/Tooltip';

interface ElementCommonProps {
    name: string;
    className?: string;
    [key: string]: unknown;
}

/**
 * Default element renderer when no custom `Element` is provided.
 * Reads `register` from the surrounding `<FormProvider>` and spreads it onto
 * a native `<input>`. Pass `elementProps={{ type: 'email' }}` etc. to override
 * the native input attributes.
 */
function DefaultInput({ name, className, ...rest }: ElementCommonProps) {
    const { register } = useFormContext();

    return (
        <input
            className={ className }
            { ...(rest as InputHTMLAttributes<HTMLInputElement>) }
            { ...register(name) }
        />
    );
}

interface Props {

    /** Dotted field path matching the form schema (`'email'`, `'address.city'`, etc.). */
    name: string;

    /** Optional override; defaults to the `forms.<last-segment>` translation key. */
    label?: ReactNode;

    /** Optional helper text rendered as a tooltip (inline layout) or small text (block). */
    description?: ReactNode;

    /** Extra classes appended to the field element's class string. */
    className?: string;

    /**
     * Swap the input element. Receives `name`, `className`, and any `elementProps`.
     * Typed loosely (any) because concrete field components (AjaxSelect, AdaptiveSelect…)
     * declare their own required props beyond the common name/className pair.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Element?: ComponentType<any>;

    /** Extra props forwarded to the element. */
    elementProps?: Record<string, unknown>;

    /** Inline layout puts label above field; block layout puts label left, field right. */
    inline?: boolean;
    required?: boolean;

    /** Rendered next to the field (e.g. "%" suffix). */
    suffix?: ReactNode;

    /** When inline, only show the error message if `verbose` is true. */
    verbose?: boolean;
}

// Walk RHF's nested error tree by a dotted path: 'a.b.c' -> errors.a?.b?.c
function getFieldError(errors: FieldErrors, name: string): { message?: string } | undefined {
    return name.split('.').reduce<unknown>((acc, key) => {
        if (acc && typeof acc === 'object' && key in (acc as Record<string, unknown>)) {
            return (acc as Record<string, unknown>)[key];
        }
        return undefined;
    }, errors) as { message?: string } | undefined;
}

export function FieldWithLabel({
    name,
    label = null,
    description = '',
    className,
    Element = DefaultInput,
    elementProps = {},
    inline = false,
    required = false,
    suffix = null,
    verbose = false,
}: Props) {
    // Input padding + the full input-styling block apply only to elements that
    // render a real <input>: the default DefaultInput, plus any custom Element
    // that opts in via a `usesInputChrome` static flag (e.g. PasswordField).
    // Custom chrome-rendering elements (Select, CheckboxToggle,
    // ExpectedFromWeekInput…) must NOT inherit these classes.
    const isDefaultInput = Element === DefaultInput;
    const usesInputChrome = isDefaultInput || (Element as { usesInputChrome?: boolean }).usesInputChrome === true;
    const effectiveClassName = className ?? (usesInputChrome ? 'px-3 py-2' : '');
    const classes = usesInputChrome
        ? 'relative block w-full appearance-none rounded-md border border-input bg-background text-foreground placeholder-muted-foreground ring-offset-primary focus-visible:outline-none focus:z-10 focus-visible:ring-1 focus:outline-none focus-visible:ring-offset-1 sm:text-sm disabled:bg-muted disabled:opacity-60 disabled:cursor-not-allowed'
        : '';
    const [
        t,
    ] = useTranslation('pages');
    const { formState: { errors } } = useFormContext();

    const effectiveLabel = label ?? (name ? t(`forms.${ name.split('.').pop() }`) : null);
    const fieldError = getFieldError(errors, name);
    const errorMessage = fieldError?.message;

    const renderError = () => {
        if (!errorMessage) {
            return null;
        }

        return <div className='text-destructive text-sm mt-1'>{ t(errorMessage) }</div>;
    };

    const renderDescription = () => {
        if (!description) {
            return null;
        }

        return <small className='text-xs text-muted-foreground mt-1'>{ description }</small>;
    };

    if (inline) {
        const labelElem = (
            <label className='text-sm font-medium text-foreground' htmlFor={ name }>
                { effectiveLabel }
            </label>
        );

        return (
            <>
                <span>
                    { description ? <Tooltip message={ description }>{ labelElem }</Tooltip> : labelElem }
                    { required && <abbr className='ml-0.5 text-primary no-underline' title='Required field'>*</abbr> }
                </span>
                <div className='mt-1 flex items-center gap-2'>
                    <Element
                        className={ clsx(effectiveClassName, classes) }
                        name={ name }
                        onWheel={ (e: React.WheelEvent<HTMLElement>) => {
                            if (e.target instanceof HTMLElement) {
                                e.target.blur();
                            }
                        } }
                        { ...elementProps }
                    />
                    { suffix ?? null }
                </div>
                { verbose && renderError() }
            </>
        );
    }

    return (
        <div>
            <div className='flex gap-x-4 items-center'>
                <label className='block w-1/4 sm:w-1/2 text-sm font-medium text-foreground' htmlFor={ name }>
                    <span>{ effectiveLabel }</span>
                    { required && <abbr className='ml-0.5 text-primary no-underline' title='Required field'>*</abbr> }
                </label>
                <div className='w-3/4 sm:w-1/2'>
                    <div className='flex items-center'>
                        <Element
                            className={ clsx(effectiveClassName, classes) }
                            name={ name }
                            onWheel={ (e: React.WheelEvent<HTMLElement>) => {
                                if (e.target instanceof HTMLElement) {
                                    e.target.blur();
                                }
                            } }
                            { ...elementProps }
                        />
                        { suffix ? <div className='ml-4'>{ suffix }</div> : null }
                    </div>
                    { renderDescription() }
                </div>
            </div>
            { renderError() }
        </div>
    );
}
